import { AppMessagesProvider } from '@common/context/appMessagesContext';
import { Auth0ProviderWithHistory } from '@common/context/auth0Context';
import { DevicesProvider } from '@common/context/DevicesContext';
import { MovementProvider } from '@common/context/MovementContext';
import { UserProvider } from '@common/context/userContext';
import { WarningsProvider } from '@common/context/warningsContext';
import Chatbot from '@components/Chatbot';
import GTMUserTracker from '@components/GTMUserTracker';
import LaunchDarklyUserTracker from '@components/LaunchDarklyUserTracker';
import { AdminProtectedRoute, ProtectedRoute } from '@components/ProtectedRoute';
import RouterLinkBridge from '@containers/AppNavigator/RouterLinkBridge';
import DataPrefetcher from '@containers/Prefetch/DataPrefetcher';
import ErrorBoundary from '@ds/components/feedback/ErrorBoundary';
import { StateProvider } from '@state';
import { lazyWithRetry } from '@utils/lazy-with-retry';
import { reportBoundaryError } from '@utils/report-boundary-error';
import React, { type ReactNode, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter as Router, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

const AdminPage = lazyWithRetry(() => import('@containers/Admin'));
const Callback = lazyWithRetry(() => import('@containers/Callback'));
const Consignments = lazyWithRetry(() => import('@containers/Consignments'));
const ConsignmentAdd = lazyWithRetry(() => import('@containers/Consignments/ConsignmentAdd'));
const ConsignmentAddSelect = lazyWithRetry(() => import('@containers/Consignments/ConsignmentAddSelect'));
const ConsignmentCopy = lazyWithRetry(() => import('@containers/Consignments/ConsignmentCopy'));
const ConsignmentCopySelectTemplate = lazyWithRetry(
    () => import('@containers/Consignments/ConsignmentCopySelectTemplate')
);
const ConsignmentEdit = lazyWithRetry(() => import('@containers/Consignments/ConsignmentEdit'));
const ConsignmentSumary = lazyWithRetry(() => import('@containers/Consignments/ConsignmentSummary'));
const DevicesMovement = lazyWithRetry(() => import('@containers/Devices'));
const Edecs = lazyWithRetry(() => import('@containers/Edecs'));
const Logout = lazyWithRetry(() => import('@containers/Logout'));
const Profile = lazyWithRetry(() => import('@containers/Profile'));
const Register = lazyWithRetry(() => import('@containers/Register'));
const Templates = lazyWithRetry(() => import('@containers/Templates'));
const TemplateAdd = lazyWithRetry(() => import('@containers/Templates/TemplateAdd'));
const TemplateEdit = lazyWithRetry(() => import('@containers/Templates/TemplateEdit'));
const Welcome = lazyWithRetry(() => import('@containers/Welcome'));

const ONE_HOUR = 60 * 60 * 1000;
const ONE_DAY = 24 * ONE_HOUR;

// Create React Query client
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: ONE_DAY, // Data stays fresh for 24 hours
            cacheTime: ONE_DAY, // Cache is kept for 24 hours
            retry: 3,
            refetchOnWindowFocus: false,
        },
    },
});

// The providers that only apply to the signed-in pages (everything except Welcome and the Auth0 callback).
const SignedInProviders: React.FC = () => (
    <StateProvider>
        <MovementProvider>
            <DevicesProvider>
                <Outlet />
            </DevicesProvider>
        </MovementProvider>
    </StateProvider>
);

// Resets the boundary on navigation so a render/query failure on one route
// doesn't keep the fallback up after the user navigates elsewhere. Protected pages
// have a closer boundary inside ProtectedRoute (so the Header stays usable); this
// one covers Welcome, Callback, Logout and the auth wrapper itself.
const RouteErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => {
    const location = useLocation();
    return (
        <ErrorBoundary
            resetKeys={[location.pathname]}
            onError={reportBoundaryError}
        >
            {children}
        </ErrorBoundary>
    );
};

const AppRouter: React.FC<{}> = () => {
    return (
        <>
            <QueryClientProvider client={queryClient}>
                <Suspense fallback={null}>
                    <WarningsProvider>
                        <Router>
                            <RouterLinkBridge>
                                <Auth0ProviderWithHistory>
                                    <GTMUserTracker />
                                    {/* Swaps LaunchDarkly's anonymous context for one carrying the
                                        signed-in email, so per-user flag targeting can match. */}
                                    <LaunchDarklyUserTracker />
                                    <UserProvider>
                                        {/* Inside UserProvider so it can send the signed-in
                                        customer's identity (token, PICs) to the bot. */}
                                        <Chatbot />
                                        <DataPrefetcher>
                                            {/* ENVDB-7836: the App_Messages banner renders once, in ProtectedRoute; pages
                                                holding a consignment publish their forms and status through this provider. */}
                                            <AppMessagesProvider>
                                                <RouteErrorBoundary>
                                                    <Routes>
                                                        <Route
                                                            path="/"
                                                            element={<Welcome />}
                                                        />
                                                        <Route
                                                            path="/callback"
                                                            element={<Callback />}
                                                        />
                                                        <Route element={<SignedInProviders />}>
                                                            <Route
                                                                path="/logout"
                                                                element={<Logout />}
                                                            />
                                                            <Route
                                                                path="/consignments/edit/:id/:section/*"
                                                                element={
                                                                    <ProtectedRoute
                                                                        component={ConsignmentEdit}
                                                                        path="/consignments/edit/:id/:section"
                                                                    />
                                                                }
                                                            />
                                                            <Route
                                                                path="/consignments/:id/movement/*"
                                                                element={
                                                                    <ProtectedRoute
                                                                        component={DevicesMovement}
                                                                        path="/consignments/:id/movement"
                                                                    />
                                                                }
                                                            />
                                                            <Route
                                                                path="/consignments/summary/:id/*"
                                                                element={
                                                                    <ProtectedRoute
                                                                        component={ConsignmentSumary}
                                                                        path="/consignments/summary/:id"
                                                                    />
                                                                }
                                                            />
                                                            <Route
                                                                path="/consignments/add-select"
                                                                element={
                                                                    <ProtectedRoute
                                                                        component={ConsignmentAddSelect}
                                                                        path="/consignments/add-select"
                                                                    />
                                                                }
                                                            />
                                                            <Route
                                                                path="/consignments/select-template"
                                                                element={
                                                                    <ProtectedRoute
                                                                        component={ConsignmentCopySelectTemplate}
                                                                        path="/consignments/select-template"
                                                                    />
                                                                }
                                                            />
                                                            <Route
                                                                path="/consignments/copy/:id"
                                                                element={
                                                                    <ProtectedRoute
                                                                        component={ConsignmentCopy}
                                                                        path="/consignments/copy/:id"
                                                                    />
                                                                }
                                                            />
                                                            <Route
                                                                path="/profile"
                                                                element={
                                                                    <ProtectedRoute
                                                                        component={Profile}
                                                                        path="/profile"
                                                                    />
                                                                }
                                                            />
                                                            <Route
                                                                path="/consignments/add"
                                                                element={
                                                                    <ProtectedRoute
                                                                        component={ConsignmentAdd}
                                                                        path="/consignments/add"
                                                                    />
                                                                }
                                                            />
                                                            <Route
                                                                path="/consignments/*"
                                                                element={
                                                                    <ProtectedRoute
                                                                        component={Consignments}
                                                                        path="/consignments"
                                                                    />
                                                                }
                                                            />
                                                            <Route
                                                                path="/templates/edit/:id/:section/*"
                                                                element={
                                                                    <ProtectedRoute
                                                                        component={TemplateEdit}
                                                                        path="/templates/edit/:id/:section"
                                                                    />
                                                                }
                                                            />
                                                            <Route
                                                                path="/templates/add/:id"
                                                                element={
                                                                    <ProtectedRoute
                                                                        component={TemplateAdd}
                                                                        path="/templates/add/:id"
                                                                    />
                                                                }
                                                            />
                                                            <Route
                                                                path="/templates"
                                                                element={
                                                                    <ProtectedRoute
                                                                        component={Templates}
                                                                        path="/templates"
                                                                    />
                                                                }
                                                            />
                                                            <Route
                                                                path="/edecs"
                                                                element={
                                                                    <ProtectedRoute
                                                                        component={Edecs}
                                                                        path="/edecs"
                                                                    />
                                                                }
                                                            />
                                                            <Route
                                                                path="/register"
                                                                element={
                                                                    <ProtectedRoute
                                                                        component={Register}
                                                                        path="/register"
                                                                    />
                                                                }
                                                            />
                                                            <Route
                                                                path="/admin"
                                                                element={
                                                                    <AdminProtectedRoute
                                                                        component={AdminPage}
                                                                        path="/admin"
                                                                    />
                                                                }
                                                            />
                                                            <Route
                                                                path="*"
                                                                element={
                                                                    <Navigate
                                                                        to="/"
                                                                        replace
                                                                    />
                                                                }
                                                            />
                                                        </Route>
                                                    </Routes>
                                                </RouteErrorBoundary>
                                            </AppMessagesProvider>
                                        </DataPrefetcher>
                                    </UserProvider>
                                </Auth0ProviderWithHistory>
                            </RouterLinkBridge>
                        </Router>
                    </WarningsProvider>
                </Suspense>
            </QueryClientProvider>
        </>
    );
};

export default AppRouter;
